import type { HiType } from '../constants';
import { ADVICE_PREFIX, FOLLOW_UP_PREFIX, GLUCOSE_CONTEXT_TEXT, type Reading } from './build';
import { CURENTIA_ASSIGNER, READING_CODES, SYSTEMS } from './codes';
import { checkDispenseRecord, checkDocument } from './check';
import type {
  Bundle,
  Coding,
  Composition,
  Condition,
  FhirResource,
  Medication,
  MedicationDispense,
  MedicationRequest,
  Observation,
  Organization,
  Patient,
  Practitioner,
  Reference,
} from './types';

/** Thrown when a document fails its structural checks. */
export class DocumentError extends Error {
  constructor(readonly issues: { path: string; message: string }[]) {
    super(`The document is not valid: ${issues.map((i) => `${i.path} ${i.message}`).join('; ')}`);
    this.name = 'DocumentError';
  }
}

function checked(bundle: Bundle, hiType: HiType) {
  const issues = checkDocument(bundle, hiType);
  if (issues.length > 0) throw new DocumentError(issues);
  const byUrl = new Map<string, FhirResource>();
  for (const entry of bundle.entry ?? []) if (entry.fullUrl && entry.resource) byUrl.set(entry.fullUrl, entry.resource);
  const resolve = <T extends FhirResource>(ref: Reference | undefined, type: T['resourceType']): T | undefined => {
    const resource = ref?.reference ? byUrl.get(ref.reference) : undefined;
    return resource?.resourceType === type ? (resource as T) : undefined;
  };
  return { composition: bundle.entry?.[0]?.resource as Composition, resolve };
}

const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' };

/**
 * Narrative XHTML as plain text: paragraphs and line breaks become new lines,
 * other tags are dropped and entities decoded. Show the result as text only,
 * never as HTML, because narrative comes from other systems.
 */
export function narrativeToText(div: string): string {
  return div
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p[^>]*>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, entity: string) => {
      if (entity.startsWith('#x') || entity.startsWith('#X')) return String.fromCodePoint(parseInt(entity.slice(2), 16));
      if (entity.startsWith('#')) return String.fromCodePoint(parseInt(entity.slice(1), 10));
      return ENTITIES[entity.toLowerCase()] ?? match;
    })
    .trim();
}

const nameOf = (resource: Patient | Practitioner | undefined) => resource?.name?.[0]?.text ?? '';

function registrationOf(practitioner: Practitioner | undefined) {
  const id = practitioner?.identifier?.find((i) => i.type?.coding?.some((c) => c.code === 'MD') && !i.system);
  return id?.value && id.assigner?.display ? { council: id.assigner.display, number: id.value } : undefined;
}

export interface MedicineView {
  requestId: string;
  name: string;
  code?: Coding;
  dosage: string;
  timesPerDay?: number;
  durationDays?: number;
  instructions?: string;
}

export interface PrescriptionView {
  id: string;
  date: string;
  patient: {
    name: string;
    gender?: NonNullable<Patient['gender']>;
    birthDate?: string;
    mobile?: string;
    /** The patient's Curentia account, when the practice linked it. */
    accountId?: string;
  };
  doctor: { name: string; registration?: { council: string; number: string }; qualifications?: string };
  practice?: { name: string; id?: string; address?: string; city?: string; phone?: string };
  diagnoses: string[];
  medicines: MedicineView[];
  notes?: string;
  advice?: string;
  followUp?: { date?: string; note?: string };
  /** The pharmacy the doctor sent it to, if any. */
  dispenser?: { name: string; practiceId?: string };
}

const curentiaId = (identifiers: { type?: { coding?: Coding[] }; value?: string; assigner?: { display?: string } }[] | undefined, code: string) =>
  identifiers?.find((i) => i.assigner?.display === CURENTIA_ASSIGNER && i.type?.coding?.some((c) => c.code === code))?.value;

/** Splits a prescription's narrative into its notes, advice and follow-up, as prescriptionNarrative wrote them. */
function narrativeParts(text: string): Pick<PrescriptionView, 'notes' | 'advice' | 'followUp'> {
  const parts: Record<'notes' | 'advice', string[]> = { notes: [], advice: [] };
  let current: 'notes' | 'advice' = 'notes';
  let followUp: PrescriptionView['followUp'];
  for (const line of text.split('\n').map((l) => l.trim()).filter(Boolean)) {
    if (line.startsWith(FOLLOW_UP_PREFIX)) {
      const rest = line.slice(FOLLOW_UP_PREFIX.length);
      const match = /^(\d{4}-\d{2}-\d{2})(?:\.\s*(.*))?$/.exec(rest);
      followUp = match?.[1] ? { date: match[1], ...(match[2] ? { note: match[2] } : {}) } : { note: rest };
      continue;
    }
    if (line.startsWith(ADVICE_PREFIX)) {
      current = 'advice';
      parts.advice.push(line.slice(ADVICE_PREFIX.length));
      continue;
    }
    parts[current].push(line);
  }
  return {
    ...(parts.notes.length > 0 ? { notes: parts.notes.join('\n') } : {}),
    ...(parts.advice.length > 0 ? { advice: parts.advice.join('\n') } : {}),
    ...(followUp ? { followUp } : {}),
  };
}

/** Reads a PrescriptionRecord into what a screen shows. Throws DocumentError for an invalid document. */
export function readPrescription(bundle: Bundle): PrescriptionView {
  const { composition, resolve } = checked(bundle, 'Prescription');
  const section = composition.section?.[0];
  const doctor = resolve<Practitioner>(composition.author[0], 'Practitioner');
  const practice = resolve<Organization>(composition.custodian, 'Organization');
  const diagnoses = new Set<string>();
  const medicines: MedicineView[] = [];
  let dispenser: PrescriptionView['dispenser'];
  for (const ref of section?.entry ?? []) {
    const request = resolve<MedicationRequest>(ref, 'MedicationRequest');
    if (!request) continue;
    const performer = request.dispenseRequest?.performer;
    if (!dispenser && performer?.display) {
      const practiceId = performer.identifier?.assigner?.display === CURENTIA_ASSIGNER ? performer.identifier.value : undefined;
      dispenser = { name: performer.display, ...(practiceId ? { practiceId } : {}) };
    }
    for (const reason of request.reasonReference ?? []) {
      const text = resolve<Condition>(reason, 'Condition')?.code?.text;
      if (text) diagnoses.add(text);
    }
    const dosage = request.dosageInstruction[0];
    const repeat = dosage?.timing?.repeat;
    const code = request.medicationCodeableConcept.coding?.[0];
    const instructions = dosage?.additionalInstruction?.[0]?.text;
    medicines.push({
      requestId: request.id ?? '',
      name: request.medicationCodeableConcept.text ?? code?.display ?? '',
      ...(code ? { code } : {}),
      dosage: dosage?.text ?? '',
      ...(repeat?.frequency && repeat.periodUnit === 'd' && repeat.period === 1 ? { timesPerDay: repeat.frequency } : {}),
      ...(repeat?.boundsDuration?.value !== undefined && repeat.boundsDuration.code === 'd'
        ? { durationDays: repeat.boundsDuration.value }
        : {}),
      ...(instructions ? { instructions } : {}),
    });
  }
  const registration = registrationOf(doctor);
  const qualifications = doctor?.qualification?.[0]?.code.text;
  const patient = resolve<Patient>(composition.subject, 'Patient');
  const accountId = curentiaId(patient?.identifier, 'PI');
  const mobile = patient?.telecom?.find((t) => t.system === 'phone')?.value;
  const practiceId = curentiaId(practice?.identifier, 'XX');
  const address = practice?.address?.[0];
  const phone = practice?.telecom?.find((t) => t.system === 'phone')?.value;
  return {
    id: bundle.identifier?.value ?? '',
    date: composition.date,
    patient: {
      name: nameOf(patient),
      ...(patient?.gender ? { gender: patient.gender } : {}),
      ...(patient?.birthDate ? { birthDate: patient.birthDate } : {}),
      ...(mobile ? { mobile } : {}),
      ...(accountId ? { accountId } : {}),
    },
    doctor: { name: nameOf(doctor), ...(registration ? { registration } : {}), ...(qualifications ? { qualifications } : {}) },
    ...(practice?.name
      ? {
          practice: {
            name: practice.name,
            ...(practiceId ? { id: practiceId } : {}),
            ...(address?.text ? { address: address.text } : {}),
            ...(address?.city ? { city: address.city } : {}),
            ...(phone ? { phone } : {}),
          },
        }
      : {}),
    diagnoses: [...diagnoses],
    medicines,
    ...(section?.text?.div ? narrativeParts(narrativeToText(section.text.div)) : {}),
    ...(dispenser ? { dispenser } : {}),
  };
}

const hasCode = (observation: Observation, coding: Coding) =>
  observation.code.coding?.some((c) => c.system === SYSTEMS.loinc && c.code === coding.code) ?? false;

function toReading(observation: Observation): Reading | null {
  const at = observation.effectiveDateTime;
  const value = observation.valueQuantity?.value;
  if (!at) return null;
  if (hasCode(observation, READING_CODES.bloodPressure)) {
    const part = (coding: Coding) =>
      observation.component?.find((c) => c.code.coding?.some((x) => x.code === coding.code))?.valueQuantity?.value;
    const systolic = part(READING_CODES.systolic);
    const diastolic = part(READING_CODES.diastolic);
    return systolic !== undefined && diastolic !== undefined ? { kind: 'blood_pressure', at, systolic, diastolic } : null;
  }
  if (value === undefined) return null;
  if (hasCode(observation, READING_CODES.pulse)) return { kind: 'pulse', at, bpm: value };
  if (hasCode(observation, READING_CODES.spo2)) return { kind: 'spo2', at, percent: value };
  if (hasCode(observation, READING_CODES.temperature)) return { kind: 'temperature', at, celsius: value };
  if (hasCode(observation, READING_CODES.weight)) return { kind: 'weight', at, kg: value };
  if (hasCode(observation, READING_CODES.bloodGlucose)) {
    const context = (Object.keys(GLUCOSE_CONTEXT_TEXT) as (keyof typeof GLUCOSE_CONTEXT_TEXT)[]).find(
      (key) => GLUCOSE_CONTEXT_TEXT[key] === observation.code.text,
    );
    return { kind: 'blood_glucose', at, mgPerDl: value, ...(context ? { context } : {}) };
  }
  return null;
}

/** Reads the readings out of a WellnessRecord. Unknown observations are skipped. */
export function readWellnessRecord(bundle: Bundle): { id: string; date: string; readings: Reading[] } {
  const { composition, resolve } = checked(bundle, 'WellnessRecord');
  const readings: Reading[] = [];
  for (const section of composition.section ?? []) {
    for (const ref of section.entry ?? []) {
      const observation = resolve<Observation>(ref, 'Observation');
      const reading = observation ? toReading(observation) : null;
      if (reading) readings.push(reading);
    }
  }
  return { id: bundle.identifier?.value ?? '', date: composition.date, readings };
}

/** One medicine in a dispensing record: what was handed over, or why not. */
export interface DispensedView {
  medicine: string;
  status: MedicationDispense['status'];
  /** What the item answers: urn:uuid: and the prescription's MedicationRequest ID, or the prescription's own identifier. */
  prescriptionRef?: string;
  /** The prescription's MedicationRequest ID, when prescriptionRef names one. */
  requestId?: string;
  quantity?: { value: number; unit: string };
  batch?: { number: string; expiry?: string };
  substituted: boolean;
  substitutionReason?: string;
  note?: string;
}

export interface DispenseView {
  id: string;
  date: string;
  patient: { name: string };
  pharmacist: { name: string; registration?: { council: string; number: string }; qualifications?: string };
  pharmacy?: { name: string; id?: string; address?: string; city?: string; phone?: string };
  items: DispensedView[];
}

const REQUEST_REF = /^urn:uuid:([0-9a-f-]{36})$/;

/** Reads a dispensing record made by buildDispenseRecord. Throws DocumentError for one that fails its checks. */
export function readDispenseRecord(bundle: Bundle): DispenseView {
  const issues = checkDispenseRecord(bundle);
  if (issues.length > 0) throw new DocumentError(issues);
  const byUrl = new Map<string, FhirResource>();
  for (const entry of bundle.entry ?? []) if (entry.fullUrl && entry.resource) byUrl.set(entry.fullUrl, entry.resource);
  const resolve = <T extends FhirResource>(ref: Reference | undefined, type: T['resourceType']): T | undefined => {
    const resource = ref?.reference ? byUrl.get(ref.reference) : undefined;
    return resource?.resourceType === type ? (resource as T) : undefined;
  };
  const items: DispensedView[] = [];
  let patient: Patient | undefined;
  let pharmacist: Practitioner | undefined;
  for (const entry of bundle.entry ?? []) {
    if (entry.resource?.resourceType !== 'MedicationDispense') continue;
    const dispense = entry.resource;
    patient ??= resolve<Patient>(dispense.subject, 'Patient');
    pharmacist ??= resolve<Practitioner>(dispense.performer?.[0]?.actor, 'Practitioner');
    const medication = resolve<Medication>(dispense.medicationReference, 'Medication');
    const quantity = dispense.quantity;
    const reason = dispense.substitution?.reason?.[0]?.text;
    const prescriptionRef = dispense.authorizingPrescription?.[0]?.identifier?.value;
    const requestId = prescriptionRef ? REQUEST_REF.exec(prescriptionRef)?.[1] : undefined;
    const lot = medication?.batch?.lotNumber;
    items.push({
      medicine: dispense.medicationCodeableConcept?.text ?? medication?.code?.text ?? dispense.medicationReference?.display ?? '',
      status: dispense.status,
      ...(prescriptionRef ? { prescriptionRef } : {}),
      ...(requestId ? { requestId } : {}),
      ...(quantity?.value !== undefined && quantity.unit ? { quantity: { value: quantity.value, unit: quantity.unit } } : {}),
      ...(lot ? { batch: { number: lot, ...(medication?.batch?.expirationDate ? { expiry: medication.batch.expirationDate } : {}) } } : {}),
      substituted: dispense.substitution?.wasSubstituted ?? false,
      ...(reason ? { substitutionReason: reason } : {}),
      ...(dispense.note?.[0]?.text ? { note: dispense.note[0].text } : {}),
    });
  }
  const organization = (bundle.entry ?? []).find((e) => e.resource?.resourceType === 'Organization')?.resource as Organization | undefined;
  const registration = registrationOf(pharmacist);
  const qualifications = pharmacist?.qualification?.[0]?.code.text;
  const address = organization?.address?.[0];
  const pharmacyId = curentiaId(organization?.identifier, 'XX');
  const phone = organization?.telecom?.find((t) => t.system === 'phone')?.value;
  return {
    id: bundle.identifier?.value ?? '',
    date: bundle.timestamp ?? '',
    patient: { name: nameOf(patient) },
    pharmacist: { name: nameOf(pharmacist), ...(registration ? { registration } : {}), ...(qualifications ? { qualifications } : {}) },
    ...(organization?.name
      ? {
          pharmacy: {
            name: organization.name,
            ...(pharmacyId ? { id: pharmacyId } : {}),
            ...(address?.text ? { address: address.text } : {}),
            ...(address?.city ? { city: address.city } : {}),
            ...(phone ? { phone } : {}),
          },
        }
      : {}),
    items,
  };
}
